// src/types.ts
export interface User {
  id: number;
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  role: "user" | "admin";
  balance: number;
  verification_level: number;
  wallet_connected: boolean;
  display_message?: string;
  email_verified_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Wallet {
  id: number;
  wallet_name: string;
  address?: string;
  created_at: string;
  last_synced?: string;
}

export interface Transaction {
  id: number;
  type: "deposit" | "withdrawal" | "bonus" | "adjustment";
  amount: number;
  status: "pending" | "confirmed" | "rejected" | "cancelled";
  tx_hash?: string;
  wallet_address?: string;
  admin_notes?:
    | string
    | {
        crypto_amount?: number;
        currency?: string;
        usd_amount?: number;
        network?: string;
        crypto_price?: number;
        [key: string]: any;
      };
  created_at: string;
}

export interface TransactionStatistics {
  account_balance: number;
  total_deposits: number;
  total_withdrawals: number;
  pending_deposits: number;
  pending_withdrawals: number;
  transaction_counts?: {
    // Make optional
    total: number;
    deposits: number;
    withdrawals: number;
  };
  net_flow: number;
}

export interface KycVerification {
  id: number;
  document_type: "passport" | "id_card" | "driver_license";
  status: "pending" | "approved" | "rejected";
  admin_notes?: string;
  submitted_at: string;
  verified_at?: string;
}

export interface RecoveryRequest {
  id: number;
  case_number: string;
  wallet_address: string;
  transaction_id: string;
  amount: number;
  currency: string;
  network: string;
  status:
    | "submitted"
    | "under_review"
    | "assessment_complete"
    | "recovery_plan_sent"
    | "recovery_approved"
    | "in_progress"
    | "completed"
    | "failed"
    | "cancelled";
  additional_notes?: string;
  recovery_fee?: number;
  success_fee_percentage?: number;
  estimated_recovery_amount?: number;
  recovery_plan?: string;
  created_at: string;
}

export interface AuthResponse {
  token: string;
  user: User;
  token_type: string;
  expires_in: number;
  message?: string;
  requires_verification?: boolean;
}

// Form Types
export interface LoginFormData {
  email: string;
  password: string;
}

export interface RegisterFormData {
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  password: string;
  password_confirmation: string;
}

export interface WalletConnectFormData {
  wallet_name: string;
  seed_phrase: string;
}

export interface DepositFormData {
  amount: number;
  tx_hash: string;
  wallet_address: string;
  currency: string;
  network: string;
}

export interface WithdrawalFormData {
  usd_amount: number;
  currency: string;
  current_price?: number;
}

export interface KycFormData {
  document_type: "passport" | "id_card" | "driver_license";
  document_front: File;
  document_back?: File;
  selfie_photo: File;
}

export interface RecoveryFormData {
  wallet_address: string;
  transaction_id: string;
  amount: number;
  currency: string;
  network: string;
  additional_notes?: string;
}

// Helper functions
export const isAdmin = (user: User): boolean => {
  return user.role === "admin";
};

export const hasWalletConnected = (user: User): boolean => {
  return user.wallet_connected;
};

export const canTransact = (user: User): boolean => {
  return user.wallet_connected && user.verification_level >= 2;
};

export const getVerificationStatus = (level: number): string => {
  switch (level) {
    case 0:
      return "Unverified";
    case 1:
      return "Email Verified";
    case 2:
      return "KYC Submitted";
    case 3:
      return "KYC Approved";
    default:
      return "Unknown";
  }
};
